import React, {
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
	CCAlert,
	CCBreadcrumbs,
	CCButton,
	CCEmptyState,
	CCModal,
	CCSpinner,
	useToast,
} from "@pymblecitizencoders/cc-ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faEye, faRepeat } from "@fortawesome/free-solid-svg-icons";
import { useEventManager } from "../hooks/useEventManager";
import { EventDetailsTab } from "../components/eventManager/EventDetailsTab";
import { EventRequirementsTab } from "../components/eventManager/EventRequirementsTab";
import { EventPermissionsTab } from "../components/eventManager/EventPermissionsTab";
import { EventTransportTab } from "../components/eventManager/EventTransportTab";
import { useEventTransport } from "../hooks/useEventTransport";
import { finalizeTransport } from "../utils/transport";
import { EventAddressTab } from "../components/eventManager/EventAddressTab";
import { EventGroupsTab } from "../components/eventManager/EventGroupsTab";
import { EventNoticesTab } from "../components/eventManager/EventNoticesTab";
import { EventAttendeesTab } from "../components/eventManager/EventAttendeesTab";
import { EventSignOffPanel } from "../components/eventManager/EventSignOffPanel";
import { EventSaveBar } from "../components/eventManager/EventSaveBar";
import { EventSidebar } from "../components/eventManager/EventSidebar";
import { EventReviewModal } from "../components/eventManager/EventReviewModal";
import { EventReviewResult } from "../components/eventManager/EventReviewResult";
import { useEventReview } from "../hooks/useEventReview";
import { EventDoc } from "../types/event";
import { changedPaths, deepEqual } from "../utils/deepEqual";
import { originOf } from "../utils/eventOrigin";
import { decodeEntities } from "../utils/htmlEntities";
import "../components/eventManager/eventManager.css";

const TABS = [
	{ key: "details", label: "Details" },
	{ key: "address", label: "Location" },
	{ key: "requirements", label: "Requirements" },
	{ key: "transport", label: "Transport" },
	/* No "Sign-off" tab. It is a status pill in the page header now — see EventSignOffPanel.
	   Worth knowing if you are re-adding it: Permissions here is carer approval
	   for a child to attend, which is a different thing from colleagues
	   releasing the event, and two tabs meaning different people is how someone
	   releases the wrong event. */
	{ key: "permissions", label: "Permissions" },
	{ key: "groups", label: "Groups" },
	{ key: "notices", label: "Notices" },
	{ key: "attendees", label: "Attendees" },
] as const;

type TabKey = (typeof TABS)[number]["key"];

/**
 * Event editor. Layout follows pymble-swipe's CreateEvent: back link and title
 * across the top, tabbed form on the left, a sidebar on the right holding the
 * sign-off state, dates,
 * featured image and Save.
 *
 * Unlike v2 — which mutates the fetched event object in place before saving —
 * this keeps a local draft and applies immutable patches, so React sees every
 * edit and the dirty flag is trustworthy. (v2's in-place mutation is called out
 * as a gotcha in its own CLAUDE.md.)
 */
export const Event: React.FC = () => {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const {
		events,
		eventsLoaded,
		eventLoadError,
		eventSaving,
		canEdit,
		permissions,
		updateEvents,
	} = useEventManager(id);

	const toast = useToast();
	const [tab, setTab] = useState<TabKey>("details");
	const [draft, setDraft] = useState<EventDoc | null>(null);
	/**
	 * The draft as it was at the moment a save succeeded, by reference.
	 *
	 * Only needed for the gap between the mutation resolving and the refetch
	 * landing. `dirty` is derived from a comparison with the FETCHED event, and
	 * in that window the fetched copy is still the pre-save one, so the editor
	 * would go on claiming unsaved changes for a moment after saying "Event
	 * saved." Cleared the instant the server's own version arrives, which is
	 * the version everything should be compared against.
	 */
	const [savedDraft, setSavedDraft] = useState<EventDoc | null>(null);
	/** Bumped on each successful save; EventReviewModal checks the event then. */
	const [checkAfterSave, setCheckAfterSave] = useState(0);
	/* The check lives here rather than in its modal because the header's
	   "N issues found" card reads the same result and opens it. */
	const review = useEventReview(draft?._id ?? "");
	const [reviewOpen, setReviewOpen] = useState(false);

	// Seed the draft once the single-event query resolves.
	useEffect(() => {
		if (!eventsLoaded) return;
		if (events && typeof events === "object" && !Array.isArray(events)) {
			setDraft(events as EventDoc);
			setSavedDraft(null);
		}
	}, [events, eventsLoaded]);

	/**
	 * The event as the SERVER currently holds it, as opposed to the draft.
	 *
	 * The same object the effect above seeds the draft from, named once because
	 * two things need it: the Notices tab's unsaved badge, and Discard, which is
	 * nothing more than going back to it.
	 */
	const savedEvent = useMemo(
		() =>
			events && typeof events === "object" && !Array.isArray(events)
				? (events as EventDoc)
				: null,
		[events],
	);

	/**
	 * Notice ids as the SERVER currently holds them, not as the draft does.
	 *
	 * The Notices tab badges anything linked but not yet written, and the tab
	 * cannot work that out for itself: switching tabs unmounts it, so a flag it
	 * kept locally would clear the moment someone looked at Attendees and came
	 * back, while the notice was still unsaved. Comparing against the fetched
	 * event is the only version of "saved" that survives that.
	 */
	const savedNoticeIds = useMemo(() => {
		const notices = Array.isArray(savedEvent?.Notices)
			? savedEvent.Notices
			: [];
		return new Set(notices.map((n) => (typeof n === "string" ? n : n._id)));
	}, [savedEvent]);

	/**
	 * Does the draft differ from the event as it is stored?
	 *
	 * DERIVED, not a flag. It used to be state set by `onChange`, which asks a
	 * different question: whether anything ever called it. A control that
	 * patches the draft with the value it already held, or a tab that writes an
	 * object back with a fresh identity, set it permanently — so the editor
	 * could sit there insisting on unsaved changes, blocking Submit and offering
	 * Discard, over a record identical to the one on the server.
	 *
	 * Compared against the FETCHED event rather than a snapshot taken on mount,
	 * so a save by anyone else that arrives while this editor is open is
	 * measured against too.
	 */
	const dirty = useMemo(() => {
		if (!draft) return false;
		if (savedDraft && draft === savedDraft) return false;
		if (!savedEvent) return false;
		if (deepEqual(draft, savedEvent)) return false;
		/* FIELD NAMES ONLY — the document carries attendee PII, so what is in a
		   field never reaches the console. "Unsaved changes" over an event nobody
		   has touched is otherwise unfalsifiable from the outside: this says which
		   field the draft parted company with the server on.

		   `warn`, not `debug`: Chrome's console hides the Verbose level by
		   default, so a `console.debug` diagnostic can be firing perfectly and
		   look like silence.

		   OPT-IN, via `?debugdirty` on the URL, rather than on in every dev build:
		   `dirty` is recomputed on each keystroke, so an always-on version prints
		   a line per character once there is a real edit and buries the case it
		   exists to catch — the draft differing before anyone has typed. */
		if (globalThis.location?.search?.toLowerCase().includes("debugdirty")) {
			console.warn(
				"[event] draft differs from saved at:",
				changedPaths(draft, savedEvent).join(", ") || "(unknown)",
			);
		}
		return true;
	}, [draft, savedEvent, savedDraft]);

	const onChange = useCallback((patch: Partial<EventDoc>) => {
		setDraft((prev) => (prev ? { ...prev, ...patch } : prev));
	}, []);

	/**
	 * Throw the draft away and go back to the saved event.
	 *
	 * Cheap because of the rule this page already follows: every edit is an
	 * immutable patch, so the fetched document is untouched and reverting is
	 * pointing at it again. v2 mutated its event in place, where the original
	 * had already been overwritten by the first keystroke and there was nothing
	 * to go back to.
	 *
	 * WHAT IT DOES NOT UNDO: anything a tab has already written through the API
	 * on its own — attendee edits, requirement notes, the publish workflow.
	 * Those are saved at the moment they happen and were never part of this
	 * draft, so they are not the caller's to take back here.
	 */
	const onRevert = useCallback(() => {
		if (!savedEvent) return;
		/* No flag to clear: pointing the draft back at the fetched event IS the
		   clean state, because that is what `dirty` compares against. */
		setDraft(savedEvent);
	}, [savedEvent]);

	const readOnly = !canEdit;

	/**
	 * The check to show: this session's if one has run, otherwise the one the
	 * API stored on the event, so the result is there on opening the event and
	 * for whoever opens it next.
	 *
	 * OUT OF DATE, not cleared, once the event is saved again: each check
	 * carries the `UpdatedAt` it read, and the stored event's own moves on with
	 * every save. Storing a check does not move it, so a check can never make
	 * itself look stale.
	 */
	const shownReview = review.result ?? savedEvent?.Review ?? null;
	const reviewStale =
		!!shownReview &&
		(shownReview.EventUpdatedAt ?? null) !== (savedEvent?.UpdatedAt ?? null);

	const { options: transportOptions } = useEventTransport();

	const onSave = useCallback(async () => {
		if (!draft) return;
		try {
			// A required acknowledgement with no text gets its default, as in v2.
			const payload = draft.Transport
				? {
						...draft,
						Transport: finalizeTransport(
							draft.Transport,
							(c) => transportOptions.find((o) => o.id === c)?.label ?? c,
						),
					}
				: draft;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			await updateEvents(payload as any);
			setSavedDraft(draft);
			/* Only a save that changed something is worth a check: the stored
			   event is otherwise what the last check already read. */
			if (dirty) setCheckAfterSave((n) => n + 1);
			toast.show({
				message: "Event saved.",
				severity: "success",
				duration: 3000,
			});
		} catch (error) {
			/* Deliberately no error detail logged or surfaced — the event payload
			   includes attendee PII, and API errors can echo the request back.
			   The STATUS is not detail, and 409 is the one worth telling apart:
			   the API pins a save on the sign-off status and the publication
			   state it read, so a 409 means somebody else moved one of those
			   while this editor was open. "Try again" is wrong advice for that
			   — a retry succeeds and quietly puts this draft's stale value back
			   over theirs. Reloading is the only answer that keeps their change. */
			const status = (error as { response?: { status?: number } })?.response
				?.status;
			toast.show({
				message:
					status === 409
						? "Somebody else changed this event while you had it open. Reload the event to see their version before saving."
						: "Could not save the event. Please try again.",
				severity: "danger",
				duration: 5000,
			});
		}
	}, [draft, dirty, updateEvents, toast, transportOptions]);

	/* The HEADING, which is chrome — not the Details tab's field, which holds
	   the stored value and writes it back. So this resolves `Rapha&#235;lle` to
	   the name it stands for while the field goes on showing exactly what is in
	   the document, and saving an unrelated edit cannot silently rewrite a title
	   nobody touched. The two do differ on such an event, deliberately: the
	   heading says what it means, the field says what it is. */
	const title = useMemo(
		() => decodeEntities(draft?.Title || "") || "Untitled event",
		[draft?.Title],
	);

	/**
	 * Identifiers behind the event, for support and debugging. v2 puts the same
	 * thing behind a click on the active breadcrumb; this is the double-click
	 * equivalent, so a stray click while reading the trail does not open it.
	 *
	 * ForeignKeys is an open map (`Record<string, unknown>` — the API adds
	 * systems over time), so it is walked rather than picking out the Edumate
	 * and Clipboard keys v2 hardcodes. Anything new the API starts returning
	 * shows up here without a code change.
	 */
	const [idsOpen, setIdsOpen] = useState(false);

	const idRows = useMemo(() => {
		const rows: { label: string; value: string }[] = [];
		const add = (label: string, value: unknown) => {
			if (value === null || value === undefined || value === "") return;
			rows.push({ label, value: String(value) });
		};

		add("Event id", draft?._id);
		add("Edumate event id", draft?.EventId);
		add("Master event id", draft?.MasterEventId);

		// Already listed above as "Edumate event id" — the top-level EventId is
		// this same value, so showing both just reads as a repeated row.
		const REDUNDANT = new Set(["ForeignKeys.Edumate.EventId"]);

		const walk = (value: unknown, path: string) => {
			if (value && typeof value === "object" && !Array.isArray(value)) {
				Object.entries(value as Record<string, unknown>).forEach(([k, v]) =>
					walk(v, path ? `${path}.${k}` : k),
				);
				return;
			}
			if (REDUNDANT.has(path)) return;
			add(path, value);
		};
		// Paths stay prefixed so it is obvious which rows came out of ForeignKeys
		// rather than off the event record itself.
		if (draft?.ForeignKeys) walk(draft.ForeignKeys, "ForeignKeys");

		// The audience groups a notice or notification targets — the ids most
		// often needed when someone asks why a notice went to the wrong people.
		add("Attendees group id", draft?.EventGroups?.Attendees?._id);
		add("Carers group id", draft?.EventGroups?.Parents?._id);

		return rows;
	}, [draft]);

	const mainPanelRef = useRef<HTMLDivElement>(null);
	const sidebarRef = useRef<HTMLDivElement>(null);
	/* Measured by the fit-to-window effect: this sits below both columns, so its
	   height is height they cannot have. */
	const originRef = useRef<HTMLParagraphElement>(null);

	const origin = useMemo(() => (draft ? originOf(draft) : ""), [draft]);
	const ready = !eventLoadError && eventsLoaded && !!draft;

	/**
	 * Fit the editor to the window: both columns are capped at the height left
	 * below them, and each scrolls inside itself. The page then never scrolls.
	 *
	 * This used to floor the form at the sidebar's height instead, on the
	 * reasoning that a form box shorter than the panel beside it looks broken.
	 * The cost was that a tall sidebar grew the page: measured with a 1148px
	 * sidebar, the window scrolled 418px whether the attendees list held 10 rows
	 * or 500 — the list is capped and contributes nothing, so the sidebar was
	 * the only thing pushing the page down. Capping the sidebar as well removes
	 * the cause rather than the symptom, and costs only a short scroll inside
	 * the sidebar on very tall content.
	 *
	 * One number serves both, so it is set on each column and inherited by the
	 * panels within. Nothing needs measuring except the viewport, which is why
	 * the ResizeObserver that used to watch the sidebar is gone.
	 *
	 * Hooks cannot sit after the loading/error early-returns below, so this is
	 * declared here and keyed on `ready` to attach once the panel is mounted.
	 */
	useLayoutEffect(() => {
		const panel = mainPanelRef.current;
		const sidebar = sidebarRef.current;
		if (!panel || !sidebar) return;

		const measure = () => {
			// Below Bootstrap's lg the two columns stack, so there is no shared
			// height to fit into — the page scrolls, as it should.
			if (window.innerWidth < 992) {
				panel.style.removeProperty("--em-editor-max");
				sidebar.style.removeProperty("--em-editor-max");
				return;
			}
			// Read the footer gutter off .em-page rather than repeating its value,
			// so the two cannot drift apart.
			const page = panel.closest<HTMLElement>(".em-page");
			const gutter = page
				? parseFloat(getComputedStyle(page).paddingBottom) || 0
				: 0;
			// Document-relative, so a scrolled page does not shrink the form.
			const top = panel.getBoundingClientRect().top + window.scrollY;
			/* The provenance footer sits BELOW both columns, so its height is height
			   they cannot have. Left out, the page overflows by exactly one line and
			   shows a window scrollbar over a single row of grey text — the same
			   failure the font-loading note below describes, from a different cause.
			   Read live rather than assumed: it is absent on most events. */
			const footer = originRef.current?.offsetHeight ?? 0;
			// Floored: a fractional top leaves a sub-pixel overhang, which is enough
			// for the browser to show a window scrollbar for 1-2px of nothing.
			const available = `${Math.floor(
				window.innerHeight - top - gutter - footer,
			)}px`;
			// Only write on change: the ResizeObserver below watches an ancestor
			// of the panel, so an unconditional write would retrigger it.
			if (panel.style.getPropertyValue("--em-editor-max") === available) return;
			panel.style.setProperty("--em-editor-max", available);
			sidebar.style.setProperty("--em-editor-max", available);
		};

		measure();

		/* Re-measure whenever anything above the panel reflows.
		 *
		 * The first pass runs before the web font has loaded, and when Inter
		 * arrives the breadcrumb above grows a couple of pixels and pushes the
		 * panel down. The cap was then stale by exactly that much — enough for
		 * the browser to show a window scrollbar over 2px of nothing.
		 *
		 * Observing .em-page also catches the panel's own resize, so `measure`
		 * only writes when the value actually changes; without that guard the
		 * observer would retrigger itself. */
		const page = panel.closest<HTMLElement>(".em-page");
		const observer = new ResizeObserver(measure);
		if (page) observer.observe(page);
		document.fonts?.ready.then(measure).catch(() => undefined);

		window.addEventListener("resize", measure);
		return () => {
			observer.disconnect();
			window.removeEventListener("resize", measure);
		};
	}, [ready]);

	if (eventLoadError) {
		return (
			<div className="em-page" data-surface="page">
				<div className="em-container em-container--narrow">
					<div className="em-panel em-notice-panel" data-surface="card">
						<CCEmptyState
							message="This event could not be loaded."
							description="It may have been removed, or you may not have access to it."
							action={
								<CCButton variant="outline" onClick={() => navigate("/home")}>
									<FontAwesomeIcon icon={faArrowLeft} /> Back to calendar
								</CCButton>
							}
						/>
					</div>
				</div>
			</div>
		);
	}

	if (!eventsLoaded || !draft) {
		return (
			<div className="em-loading-block">
				<CCSpinner size="lg" label="Loading event..." />
			</div>
		);
	}

	return (
		<div className="em-page" data-surface="page">
			<div className="em-container">
				{/* Breadcrumb rather than a Back button: it says where you are as
				    well as offering the way out, and the trail is the same on
				    every event so the exit is always in the same place.
				    `onClick` rather than `href` keeps it on the router.
				    The first crumb renders as a home icon — see .em-editor-crumbs
				    for why that is done in CSS rather than passed in here.

				    This is also the page's only title now: the CCPageHeader that
				    used to sit below repeated the event name the trail already
				    ends with. */}
				{/* Double-clicking the event name opens the id list. CCBreadcrumb
				    takes no event handlers, so the listener sits on a wrapper and
				    checks the target — `aria-current="page"` is the markup contract
				    for "the crumb you are on", so it holds even if cc-ui renames
				    its classes. */}
				<div className="em-editor-head">
					<div
						onDoubleClick={(e) => {
							if ((e.target as HTMLElement).closest('[aria-current="page"]')) {
								setIdsOpen(true);
							}
						}}
					>
						<CCBreadcrumbs
							className="em-editor-crumbs"
							items={[
								{ label: "Event Manager", onClick: () => navigate("/home") },
								{ label: title },
							]}
						/>
					</div>

					<div className="em-editor-head__actions">
						{/* The event check's result, at the top right where it is seen
						    without scrolling the sidebar. */}
						<EventReviewResult
							readOnly={readOnly}
							issues={shownReview?.Issues ?? null}
							stale={reviewStale}
							aiUsed={shownReview?.AiUsed ?? true}
							running={review.running}
							onOpen={() => setReviewOpen(true)}
						/>
						{/* Says this event is one of a run, and which. Saving here
						    changes this event only, so the hover text says so rather
						    than leaving it to be found out. */}
						{draft?.Series && (
							<span
								className="em-series-pill"
								title="Part of a recurring series. Changes you save here apply to this event only."
							>
								<FontAwesomeIcon icon={faRepeat} aria-hidden="true" />
								<span>
									Recurring event, {draft.Series.Position} of{" "}
									{draft.Series.Count}
								</span>
							</span>
						)}
						{/* One non-wrapping item, so a narrow header wraps the group as a
						    whole and never leaves the status on one line with Save on the
						    next. */}
						<div className="em-editor-head__end">
							{/* The status, beside Save. The chain and Submit open from it. Withdrawing
							    a released event is `event_admin` or `event_publisher`, which
							    is not the same permission as being able to edit it. */}
							{draft && (
								<EventSignOffPanel
									eventId={draft._id}
									readOnly={readOnly}
									eventType={draft.EventType}
									canWithdraw={canEdit || permissions.event_publisher === true}
									/* Submit sends the STORED event, so an unsaved draft must
									   not be sendable. */
									dirty={dirty}
								/>
							)}
							{/* Last, at the right-hand end. Editors only. */}
							{draft && !readOnly && (
								<EventSaveBar
									event={draft}
									saving={eventSaving}
									dirty={dirty}
									onSave={onSave}
									onRevert={onRevert}
								/>
							)}
						</div>
					</div>
				</div>

				{/* The event check: no control of its own, it runs on save and
				    the header's issues card opens it. Editors only: the review endpoint needs
				    `event_admin`, which is what `readOnly` reflects. */}
				{!readOnly && draft?._id && (
					<EventReviewModal
						eventId={draft._id}
						dirty={dirty}
						checkAfterSave={checkAfterSave}
						review={review}
						result={shownReview}
						stale={reviewStale}
						open={reviewOpen}
						onOpenChange={setReviewOpen}
					/>
				)}

				<CCModal
					open={idsOpen}
					onClose={() => setIdsOpen(false)}
					title="Event ids"
					footer={
						<CCButton variant="outline" onClick={() => setIdsOpen(false)}>
							Close
						</CCButton>
					}
				>
					{idRows.length ? (
						<dl className="em-devinfo">
							{idRows.map((row) => (
								<React.Fragment key={row.label}>
									<dt>{row.label}</dt>
									<dd>{row.value}</dd>
								</React.Fragment>
							))}
						</dl>
					) : (
						<p className="em-muted">
							This event carries no identifiers beyond its own record.
						</p>
					)}
				</CCModal>

				{/* Was an inline muted note beside the title. CCAlert states the
				    restriction plainly instead — someone who cannot save should
				    find that out before filling the form in, not after. */}
				{readOnly && (
					<CCAlert tone="info" className="em-readonly-alert">
						<FontAwesomeIcon icon={faEye} /> View only — you do not have edit
						access to this event.
					</CCAlert>
				)}

				<div className="em-grid">
					<div className="em-col-lg-9">
						<div
							className="em-panel em-editor-panel"
							data-surface="card"
							ref={mainPanelRef}
						>
							<div className="em-tabbar" role="tablist">
								{TABS.map((t) => (
									<CCButton
										key={t.key}
										/* `underline`, not `tab`: cc-ui's tab
										   variant is a pill that fills when
										   selected. This is section navigation,
										   which is what the underline variant is
										   for — see CCTabsProps. */
										variant="underline"
										role="tab"
										selected={tab === t.key}
										aria-selected={tab === t.key}
										onClick={() => setTab(t.key)}
									>
										{t.label}
									</CCButton>
								))}
							</div>

							<div
								className={
									// The Attendees tab manages its own height — the
									// roll scrolls inside its own box — so this
									// wrapper must not also be a scroll container.
									// A CCMenu popup is absolutely positioned, and an
									// absolutely positioned box still counts towards
									// its scroller's overflow, so on a short panel
									// (an event with no attendees yet) opening any of
									// the toolbar menus grew this box and clipped the
									// menu at its edge.
									tab === "attendees"
										? "em-editor-tabpanel em-editor-tabpanel--overflowing"
										: "em-editor-tabpanel"
								}
							>
								{tab === "details" && (
									<EventDetailsTab
										event={draft}
										readOnly={readOnly}
										onChange={onChange}
									/>
								)}
								{tab === "requirements" && (
									<EventRequirementsTab
										event={draft}
										readOnly={readOnly}
										onChange={onChange}
									/>
								)}
								{tab === "permissions" && (
									<EventPermissionsTab
										event={draft}
										readOnly={readOnly}
										onChange={onChange}
									/>
								)}
								{tab === "transport" && (
									<EventTransportTab
										event={draft}
										readOnly={readOnly}
										onChange={onChange}
									/>
								)}
								{tab === "address" && (
									<EventAddressTab
										event={draft}
										readOnly={readOnly}
										onChange={onChange}
									/>
								)}
								{tab === "groups" && (
									<EventGroupsTab
										event={draft}
										readOnly={readOnly}
										onChange={onChange}
									/>
								)}
								{tab === "notices" && (
									<EventNoticesTab
										event={draft}
										readOnly={readOnly}
										onChange={onChange}
										savedNoticeIds={savedNoticeIds}
									/>
								)}

								{tab === "attendees" && (
									<EventAttendeesTab
										event={draft}
										// event_admin gates every attendee write.
										// The API enforces it independently; this
										// only avoids offering an action that
										// would come back 403.
										canEdit={canEdit}
										// Groups are still the bulk route for
										// attendees, so this stays alongside
										// adding named people.
										onAddAttendees={() => setTab("groups")}
									/>
								)}
							</div>
						</div>
					</div>

					<div className="em-col-lg-3">
						{/* Carries the shared height cap for the sidebar's two cards —
						    see the fit-to-window effect above, and the note in
						    EventSidebar on why the cap lives here rather than on each
						    card. */}
						<div ref={sidebarRef} className="em-sidebar-stack">
							<EventSidebar
								event={draft}
								readOnly={readOnly}
								onChange={onChange}
							/>
						</div>
					</div>
				</div>

				{/* Provenance, centred at the foot of the page. Read-only, and an
				    answer ABOUT the event rather than a part of it, so it belongs
				    under both columns rather than inside either: in the sidebar it
				    read as a caption to the sign-off card, and at the end of the
				    Details tab it read as one more form field.

				    ITS HEIGHT IS SUBTRACTED FROM THE FIT-TO-WINDOW CAP — see the
				    effect above. The two columns are capped to the space left in the
				    window, so anything added below them in flow is height nothing
				    accounted for, and the page grows a scrollbar over a single line
				    of grey text. That is the same phantom-scrollbar failure the
				    effect's own comments describe twice.

				    Rendered only when there is something to say — see originOf. */}
				{origin && (
					<p ref={originRef} className="em-origin em-page-origin">
						{origin}
					</p>
				)}
			</div>
		</div>
	);
};
